/*
 * Copyright Mimic Networks, Inc. 2026.
 */

export interface VariantInfo {
  label: string;
  schema: Record<string, unknown>;
}

/**
 * Extracts variant info from a oneOf/anyOf schema.
 * Returns null if the schema doesn't use oneOf/anyOf.
 */
export function extractVariants(
  schema: Record<string, unknown>,
  schemas: Record<string, unknown>,
): VariantInfo[] | null {
  // Resolve $ref first
  let resolved = schema;

  if (schema.$ref) {
    const refPath = (schema.$ref as string).replace('#/components/schemas/', '');
    resolved = (schemas[refPath] as Record<string, unknown>) || schema;
  }

  const variants = resolved.oneOf || resolved.anyOf;
  if (!variants) return null;

  const discriminator = resolved.discriminator as { propertyName?: string } | undefined;
  const propName = discriminator?.propertyName;

  return (variants as Record<string, unknown>[]).map((variant, i) => {
    const resolvedVariant = resolveSchema(variant, schemas);

    // Try to find a label from the discriminator field's enum value
    let label = `Variant ${i + 1}`;

    if (propName && resolvedVariant.properties) {
      const discProp = (resolvedVariant.properties as Record<string, Record<string, unknown>>)[propName];

      if (discProp?.enum) {
        label = String((discProp.enum as unknown[])[0]);
      }
    }

    // Fallback: look for a "type" field with enum if no discriminator
    if (label === `Variant ${i + 1}` && !propName && resolvedVariant.properties) {
      const typeProp = (resolvedVariant.properties as Record<string, Record<string, unknown>>).type;

      if (typeProp?.enum) {
        label = String((typeProp.enum as unknown[])[0]);
      }
    }

    // Fallback: use x-go-type-name or title
    if (label === `Variant ${i + 1}`) {
      if (variant['x-go-type-name']) label = String(variant['x-go-type-name']);
      else if (resolvedVariant.title) label = String(resolvedVariant.title);
    }

    return { label, schema: resolvedVariant };
  });
}

export function resolveSchema(
  schema: Record<string, unknown>,
  schemas: Record<string, unknown>,
  depth = 0,
): Record<string, unknown> {
  if (depth > 8) return schema;

  if (schema.$ref) {
    const refPath = (schema.$ref as string).replace('#/components/schemas/', '');
    const resolved = schemas[refPath] as Record<string, unknown>;
    if (resolved) return resolveSchema(resolved, schemas, depth + 1);
    return { type: 'object', title: refPath };
  }

  if (schema.allOf) {
    const merged: Record<string, unknown> = { type: 'object', properties: {}, required: [] };

    (schema.allOf as Record<string, unknown>[]).forEach((sub) => {
      const resolved = resolveSchema(sub, schemas, depth + 1);

      if (resolved.properties) {
        merged.properties = {
          ...(merged.properties as Record<string, unknown>),
          ...(resolved.properties as Record<string, unknown>),
        };
      }

      if (resolved.required) {
        merged.required = [...(merged.required as string[]), ...(resolved.required as string[])];
      }
    });

    return merged;
  }

  // For oneOf/anyOf, resolve the first variant as default.
  // Callers that need variant selection should use extractVariants() instead.
  const variantList = schema.oneOf || schema.anyOf;

  if (variantList) {
    const first = (variantList as Record<string, unknown>[])[0];
    if (first) return resolveSchema(first, schemas, depth + 1);
    return schema;
  }

  if (schema.type === 'array' && schema.items) {
    return {
      ...schema,
      items: resolveSchema(schema.items as Record<string, unknown>, schemas, depth + 1),
    };
  }

  if (schema.properties) {
    const resolved = Object.entries(schema.properties as Record<string, Record<string, unknown>>).reduce<
      Record<string, unknown>
    >((acc, [key, val]) => {
      acc[key] = resolveSchema(val, schemas, depth + 1);
      return acc;
    }, {});
    return { ...schema, properties: resolved };
  }

  return schema;
}

export function generateExample(schema: Record<string, unknown>, schemas: Record<string, unknown>, depth = 0): unknown {
  if (depth > 8) return {};

  if (schema.$ref) {
    const refPath = (schema.$ref as string).replace('#/components/schemas/', '');
    const resolved = schemas[refPath] as Record<string, unknown>;
    if (resolved) return generateExample(resolved, schemas, depth + 1);
    return {};
  }

  const variants = schema.oneOf || schema.anyOf;

  if (variants) {
    const first = (variants as Record<string, unknown>[])[0];
    if (first) return generateExample(first, schemas, depth + 1);
    return {};
  }

  if (schema.allOf) {
    const merged: Record<string, unknown> = {};

    (schema.allOf as Record<string, unknown>[]).forEach((sub) => {
      const result = generateExample(sub, schemas, depth + 1);
      if (result && typeof result === 'object') Object.assign(merged, result);
    });

    return merged;
  }

  if (schema.example !== undefined) return schema.example;
  if (schema.default !== undefined) return schema.default;

  if (schema.enum) {
    return (schema.enum as unknown[])[0];
  }

  if (schema.type === 'array') {
    const items = schema.items as Record<string, unknown> | undefined;

    if (items) {
      if (items.example !== undefined) return [items.example];

      const itemExample = generateExample(items, schemas, depth + 1);
      if (itemExample !== undefined && itemExample !== '') return [itemExample];
    }

    return [];
  }

  if (schema.type === 'object' || schema.properties) {
    const props = schema.properties as Record<string, Record<string, unknown>> | undefined;
    if (!props) return {};

    const obj: Record<string, unknown> = {};

    Object.entries(props).forEach(([key, prop]) => {
      obj[key] = generateExample(prop, schemas, depth + 1);
    });

    return obj;
  }

  if (schema.type === 'string') return '';
  if (schema.type === 'integer' || schema.type === 'number') return 0;
  if (schema.type === 'boolean') return false;

  return '';
}
